/**
 * Auth Error Reporter
 *
 * Reports critical auth errors via PostgREST RPC (log_auth_error function).
 * Errors are stored in the auth_errors table and processed by n8n for Discord alerts.
 */

import { logger } from '@/lib/logger';
import { supabase } from '@/lib/supabase';

interface AuthErrorReport {
  path: string;
  method?: string;
  status: number;
  error_message: string;
  error_code?: string;
  referer?: string;
  request_id?: string;
}

/**
 * Reports a critical auth error to the backend.
 * This is a fire-and-forget operation - errors in reporting are logged but don't affect the user flow.
 */
export async function reportAuthError(error: AuthErrorReport): Promise<void> {
  // Only report server errors (5xx) and critical auth failures
  const shouldReport = (
    error.status >= 500 ||
    error.error_code === 'unexpected_failure' ||
    error.error_message?.toLowerCase().includes('database error') ||
    error.error_message?.toLowerCase().includes('internal')
  );

  if (!shouldReport) {
    logger.debug('Auth error not critical, skipping report', { code: error.error_code, status: error.status });
    return;
  }

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error: rpcError } = await (supabase.rpc as any)('log_auth_error', {
      p_error_path: error.path,
      p_error_method: error.method || 'POST',
      p_error_status: error.status,
      p_error_message: error.error_message,
      p_error_code: error.error_code || null,
      p_referer: window.location.href,
      p_request_id: error.request_id || null,
      p_user_agent: navigator.userAgent,
    });

    if (rpcError) {
      logger.warn('Failed to report auth error', { error: rpcError.message });
    } else {
      logger.debug('Auth error reported successfully');
    }
  } catch (err) {
    // Silently fail - we don't want error reporting to affect user experience
    logger.warn('Error reporting auth error', { error: err });
  }
}

/**
 * Creates an auth error report from a Supabase auth error.
 *
 * @param supabaseError The error from Supabase auth
 * @param path The auth path (e.g., '/signup', '/token')
 * @returns An AuthErrorReport object
 */
export function createAuthErrorReport(
  supabaseError: { message: string; status?: number; code?: string; name?: string },
  path: string
): AuthErrorReport {
  // Try to extract status code from error
  let status = supabaseError.status || 500;

  // Check error message for status hints
  const statusMatch = supabaseError.message?.match(/(\d{3}):/);
  if (statusMatch) {
    status = parseInt(statusMatch[1], 10);
  }

  return {
    path,
    method: 'POST',
    status,
    error_message: supabaseError.message || 'Unknown error',
    error_code: supabaseError.code || supabaseError.name || undefined,
  };
}
